import { merge } from 'lodash-es'
import { tooltipGeneralConfig } from '/@/components/BaseChart/global'

/**
 * 千分位分隔
 * input: 123456789 ouput: 123,456,789
 * @param num number or string num
 * @param symbol 默认","分隔
 * @returns {string}
 */
export const thousandsFormat = (num: number | string, symbol = ',') => {
  return (num || '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, symbol)
}

/**
 * 根据设置的 type 类型获取对应的图标
 * @param color
 * @param seriesType
 */
export const generateSeriesIcon = ({ color = '', seriesType = '' } : { color?: string, seriesType?: string }) => {
  const seriesIconEnum: anyObj = {
    line: 'line',
    bar: 'rect',
    pie: 'circle',
    radar: 'line',
    scatter: 'circle',
  }
  const className = seriesIconEnum[seriesType] || ''
  return `<span class="sino-tooltip-icon ${className}" style="background-color: ${color || 'transparent'}"></span>`
}

/**
 * 数值格式化适配器，优先使用外部传入的格式化方法，不存在时返回默认值
 * @param item 待格式化对象 eg: { name: 'A', value: 123 }
 * @param formatter 外部格式化方法
 * @param props item 待格式化对象数值的key配置
 */
export const formatValueAdapter = (item: anyObj, formatter?: Fn, props?: { value?: string }) => {
  const defaultProps = { value: 'value' }
  const mergeProps = merge({}, defaultProps, props || {})

  // ?? 运算符当然 输入: 0 || '-'，输出: 0，如果希望 0 返回 '-'，需要使用 || 运算符
  return (formatter ? formatter(item) : (item[mergeProps.value] ?? '-'))
}

const getTooltipItemsGroupData = (data: anyObj[]): Array<anyObj[]> => {
  if (!data?.length) {
    return []
  }

  const groupCount = 12
  const groupData: Array<anyObj[]> = []
  const less = data.length % groupCount
  const fullGroupCount = Math.floor(data.length / groupCount)
  for (let i = 0; i < fullGroupCount; i++) {
    groupData[i] = data.slice(i * groupCount, (i + 1) * groupCount)
  }

  if (less) {
    groupData[fullGroupCount] = data.slice(fullGroupCount * groupCount)
  }

  return groupData
}

const objectStyleToString = (styles: anyObj) => {
  return Object.keys(styles).map(key => `${key}: ${styles[key]}`).join(';')
}

/**
 * Tooltip项的通用生成函数
 * @param data 数据数组
 * @param getLabel 获取标签的方法
 * @param formatter 外部格式化方法
 */
export const generateTooltipItems = (
  data: anyObj[],
  getLabel: (item: anyObj) => string,
  formatter?: Fn,
  extraConfig?: { tooltipDataReverse?: boolean; tooltipItemHighLightFn?: Fn; tooltipValueFormatterFn?: Fn }
) => {
  if (!data?.length) {
    return ''
  }

  const itemList: string[] = []
  const groupData = getTooltipItemsGroupData(data)
  for (let groupIndex = 0; groupIndex < groupData.length; groupIndex++) {
    const groupList: string[] = []
    for (let i = 0; i < groupData[groupIndex].length; i++) {
      const item = groupData[groupIndex][i] || {}
      const { seriesType, color } = item
      groupList.push(`
      <div class="tooltip-item ${extraConfig?.tooltipItemHighLightFn?.(item) ? 'hight-light' : '' }">
        <div class="tooltip-item-label">${generateSeriesIcon({ color, seriesType })}${getLabel(item)}</div>
        <div class="tooltip-item-value">${formatValueAdapter(item, extraConfig?.tooltipValueFormatterFn?.(item) || formatter)}</div>
      </div>`)
    }
    itemList.push(`<div>${groupList.join('')}</div>`)
  }

  const wrapperStyle = {
    'display': 'grid',
    'grid-auto-flow': 'row dense',
    'gap': '16px',
    'grid-template-columns': `repeat(${groupData.length}, minmax(0, 1fr))`
  }
  return `<div style="${objectStyleToString(wrapperStyle)}">${itemList.join('')}</div>`

  // const itemList: string[] = []
  // for (let i = 0; i < data?.length; i++) {
  //   const item = data[i] || {}
  //   const { seriesType, color } = item
  //   itemList.push(`
  //     <div class="tooltip-item ${extraConfig?.tooltipItemHighLightFn?.(item) ? 'hight-light' : '' }">
  //       <div class="tooltip-item-label">${generateSeriesIcon({ color, seriesType })}${getLabel(item)}</div>
  //       <div class="tooltip-item-value">${formatValueAdapter(item, formatter)}</div>
  //     </div>`)
  // }
  // return itemList.join('')
}

/**
 * Tooltip内容通用生成函数
 * @param data
 * @param getLabel
 * @param formatter
 * @param name
 */
export const generateTooltipContent = (
  data: anyObj[],
  getLabel: Fn,
  formatter?: Fn,
  name?: string,
  extraConfig?: { tooltipDataReverse?: boolean; tooltipItemHighLightFn?: Fn; tooltipValueFormatterFn?: Fn }
) => {
  return `
    <div class="sino-tooltip-inner">
      <div class="tooltip-title ${name ? 'flex' : 'hidden'}">${name}</div>
      <div class="tooltip-content">${generateTooltipItems(data, getLabel, formatter, extraConfig)}</div>
    </div>`
}

/**
 * 通用自定义Tooltip内容
 * 多用于多系列数据，例如多条折线图的多组数据对比显示
 * @param formatter
 */
export const generateGeneralTooltip = (
  formatter?: Fn,
  extraConfig?: { tooltipDataReverse?: boolean; tooltipItemHighLightFn?: Fn; tooltipValueFormatterFn?: Fn; axisValueSuffix?: string }
) => {
  return {
    ...tooltipGeneralConfig,
    formatter: (params: any) => {
      // console.log('params:', params)
      let data: anyObj[] = Array.isArray(params) ? params : [ params ]
      const { tooltipDataReverse } = extraConfig || {}
      tooltipDataReverse && (data = data.reverse())

      const axisValue = data[0]?.axisValue || ''
      const renderLabel = (item: anyObj) => item.seriesName || ''

      return generateTooltipContent(data, renderLabel, formatter, axisValue ? `${axisValue}${extraConfig?.axisValueSuffix || ''}` : axisValue, extraConfig)
    }
  }
}

/**
 * 基础自定义Tooltip内容
 * 多用于单柱状图、饼图、环形图单个数据显示
 * @param formatter
 */
export const generateBaseTooltip = (formatter?: Fn) => {
  return {
    ...tooltipGeneralConfig,
    formatter: (params: any) => {
      const data: anyObj[] = Array.isArray(params) ? params : [ params ]
      const renderLabel = (item: anyObj) => item.name || ''

      return generateTooltipContent(data, renderLabel, formatter)
    }
  }
}

/**
 * 雷达图自定义Tooltip内容
 * @param formatter
 * @param indicator
 */
export const generateRadarTooltip = (formatter?: Fn, indicator?: (string | undefined)[]) => {
  return {
    ...tooltipGeneralConfig,
    formatter: (params: any) => {
      const name = params.name || ''
      const color = params.color
      const seriesType = params.seriesType
      const data = (params?.value || []).map((item: any, index: number) => {
        return {
          value: item,
          indicatorName: (indicator || [])[index] || '',
          color,
          seriesType
        }
      })
      const renderLabel = (item: anyObj) => item.indicatorName || ''

      return generateTooltipContent(data, renderLabel, formatter, name)
    }
  }
}

export type TooltipItemHighLightInfo = {
  seriesIndex: number | null,
  dataIndex: number | null
}

export const TooltipItemHighLightDefaultInfo: TooltipItemHighLightInfo = {
  seriesIndex: null,
  dataIndex: null
}

export const tooltipItemCommonHighLightFn = (tooltipItemHighLightInfo: TooltipItemHighLightInfo, tooltipItem: anyObj) => {
  const { seriesIndex, dataIndex } = tooltipItem
  return Object.keys(tooltipItemHighLightInfo)
    .every(key => tooltipItemHighLightInfo[key as keyof typeof tooltipItemHighLightInfo] !== null)
    && tooltipItemHighLightInfo.seriesIndex == seriesIndex
    &&  tooltipItemHighLightInfo.dataIndex === dataIndex
}
